/**
 * Chat Input Component - קלט צ'אט
 * Message input with validation and rate limiting
 */

import { useState, useRef, useEffect } from 'react'
import { Send, AlertCircle } from 'lucide-react'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  remainingMessages?: number
  maxMessages?: number
  placeholder?: string
  /**
   * Hides the hourly-quota footer and warnings. The student learning tool is
   * governed by a spend allowance, not the advisory hourly quota, so showing
   * "50 messages per hour" there would state a limit that isn't the real one.
   */
  hideQuotaInfo?: boolean
}

export default function ChatInput({
  onSend,
  disabled = false,
  remainingMessages = 50,
  maxMessages = 50,
  placeholder,
  hideQuotaInfo = false,
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const maxLength = 2000
  const isAtLimit = remainingMessages <= 0
  const isLowMessages = remainingMessages <= 10

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [message])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedMessage = message.trim()

    // Validation
    if (!trimmedMessage) {
      setError('נא להזין הודעה')
      return
    }

    if (trimmedMessage.length < 3) {
      setError('ההודעה קצרה מדי (מינימום 3 תווים)')
      return
    }

    if (trimmedMessage.length > maxLength) {
      setError(`ההודעה ארוכה מדי (מקסימום ${maxLength} תווים)`)
      return
    }

    if (isAtLimit) {
      setError('הגעת למגבלת ההודעות לשעה (50). נסה שוב מאוחר יותר.')
      return
    }

    // Send message
    onSend(trimmedMessage)
    setMessage('')
    setError(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="border-t border-gray-200 bg-white px-3 py-2 flex-shrink-0">
      {/* Rate limit warning */}
      {!hideQuotaInfo && (isAtLimit || isLowMessages) && (
        <div className={`alert mb-2 text-xs ${isAtLimit ? 'alert-error' : 'alert-warning'}`}>
          <AlertCircle size={16} aria-hidden="true" />
          <div className="flex-1">
            {isAtLimit ? (
              <span>
                <strong>הגעת למגבלת ההודעות.</strong> מותר לשלוח עד {maxMessages} הודעות
                לשעה. נסה שוב בעוד זמן מה.
              </span>
            ) : (
              <span>
                נותרו לך <strong>{remainingMessages}</strong> הודעות לשעה זו
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert-error mb-2 text-xs" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {/* Input form */}
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <div className="flex-1">
          <label htmlFor="chat-message-input" className="sr-only">הקלדת הודעה</label>
          <textarea
            id="chat-message-input"
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="הקלדת הודעה"
            placeholder={
              isAtLimit && !hideQuotaInfo
                ? 'הגעת למגבלת ההודעות...'
                : placeholder || 'שאל אותי כל שאלה על השקעות...'
            }
            disabled={disabled || (isAtLimit && !hideQuotaInfo)}
            rows={1}
            className="input resize-none max-h-28 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
            style={{ minHeight: '44px', fontSize: '16px' }}
          />
          {message.length > maxLength * 0.9 && (
            <div className="flex justify-end mt-0.5">
              <span className="text-xs text-red-600 font-medium">
                {message.length} / {maxLength}
              </span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={disabled || (isAtLimit && !hideQuotaInfo) || !message.trim()}
          className="btn btn-primary flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ height: '44px', minWidth: '44px' }}
          aria-label="שליחה"
        >
          <Send size={20} aria-hidden="true" />
        </button>
      </form>

      {/* Usage info */}
      {!hideQuotaInfo && (
        <div className="text-[10px] text-gray-400 mt-1 text-center">
          מוגבל ל-{maxMessages} הודעות לשעה
        </div>
      )}
    </div>
  )
}
